import { inject, Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { QuestionDialogComponent } from '@app/shared/components/question-dialog/question-dialog.component';
import { QuestionCloseResult } from '@app/shared/models/components/question-close-result';
import {
    CheckinsService,
    DeliveryNoteBundlesService,
    DeliveryNotesService,
    FineDiscrepanciesService,
    ToursService,
    PdfService,
} from 'api/services';
import { BundleGroupsService, CheckinsV3Service } from 'api/addons-v3/services';
import { AppStore } from '@app/stores/appStore';
import { StorageService } from '@app/core/services/client-services/storage-service/storage.service';
import { CurrentSiteRole } from '@app/shared/models/authentication/session';
import { SessionService } from '@app/core/services/client-services/session-service/session.service';

import { NgxSpinnerService } from 'ngx-spinner';
import { ErrorHandler } from '@app/core/services/error-handler.service';
import { ViewBundleDialogComponent } from '@app/shared/components/view-bundle-dialog/view-bundle-dialog.component';
import {
    CheckinFinishedEventPayload,
    CheckinKeyAndDeliveryNoteKeyLookup,
    ConsigneeDeliveryNoteBundle,
    DeliveryNoteKeyAndCheckinKeyLookup,
    Event as ApiEvent,
    EventPayload,
    GetConsigneeTour,
    UserRoles,
} from 'api/models';
import { ShowPdfDialogComponent } from '@app/shared/components/show-pdf-dialog/show-pdf-dialog.component';
import { ToastService } from '@app/core/services/toast.service';
import { TranslateService } from '@ngx-translate/core';
import { SearchStore } from '@app/stores/searchStore';
import { SelectionStore } from '@app/stores/selectionStore';
import {
    BundleGroupData,
    BundleGroupsStore,
} from '@app/stores/bundleGroupsStore';
import { Router } from '@angular/router';
import { RoutingEndpoints } from '@app/shared/models/routing/routing-endpoints';
import { ApiInterceptor } from '@app/core/interceptors/api.interceptor';
import {
    DeliveryNoteData,
    FolderData,
    TourData,
    downloadBlobAsFile,
} from '@app/shared/models/table-data-types';
import { PdfService as PdfServiceAddons } from '../../../../api/addons/services/pdf.service';
import { hasConsigneePdf } from '@app/core/stores/permissionsStore';
import { StrictHttpResponse } from '../../../../api/strict-http-response';
import { ConfirmationDialogComponent } from '@app/shared/components/confirmation-dialog/confirmation-dialog.component';

// Type guard to check if payload has checkinKey
function hasCheckinKey(
    payload: EventPayload
): payload is CheckinFinishedEventPayload {
    return (
        payload &&
        typeof payload === 'object' &&
        'checkinKey' in payload &&
        typeof (payload as CheckinFinishedEventPayload).checkinKey === 'string'
    );
}

/**
 * Structural instead of `Pick<CheckinKeyAndDeliveryNoteKeyLookup, …>`: the
 * backend that introduced `selfCheckinDataSet` on this lookup is newer than the
 * swagger snapshots in `openapi-jsons/` and `e2e/api-spec/`, from which
 * `generate:api:prod` / `:e2e` build the client. An optional property compiles
 * against both vintages and reads the field once it is generated.
 */
type SelfCheckinLookupEntry = Pick<
    CheckinKeyAndDeliveryNoteKeyLookup,
    'selfCheckinDataSet' | 'checkinStatus'
> & { selfCheckinDataSet?: boolean };

function hasActiveSelfCheckin(
    checkins: readonly SelfCheckinLookupEntry[] | null | undefined
): boolean {
    return !!checkins?.some(
        checkin =>
            checkin.selfCheckinDataSet === true &&
            checkin.checkinStatus !== 'finished'
    );
}

@Injectable({
    providedIn: 'root',
})
export class ConsigneeViewActionsService {
    private dialog = inject(MatDialog);
    private spinner = inject(NgxSpinnerService);
    private errorHandler = inject(ErrorHandler);
    private searchStore = inject(SearchStore);
    private appStore = inject(AppStore);
    private bundleGroupsService = inject(BundleGroupsService);
    private bundleGroupsStore = inject(BundleGroupsStore);
    private selectionStore = inject(SelectionStore);

    constructor(
        private deliveryNotesService: DeliveryNotesService,
        private pdfService: PdfService,
        private pdfServiceAddons: PdfServiceAddons,
        private fineDiscrepanciesService: FineDiscrepanciesService,
        private toastService: ToastService,
        private translateService: TranslateService,
        private checkinsService: CheckinsService,
        private checkinsV3Service: CheckinsV3Service,
        private toursService: ToursService,
        private router: Router,
        private apiInterceptor: ApiInterceptor,
        private deliveryNoteBundlesService: DeliveryNoteBundlesService,
        private sessionService: SessionService,
        private storageService: StorageService
    ) {}

    /**
     * Return the url of the delivery note pdf for downloading instantly
     * @param bundle: DeliveryNoteBundleOverview
     */
    async downloadDeliveryBundle(bundle: FolderData): Promise<void> {
        this.spinner.show();
        try {
            const response = await lastValueFrom(
                this.deliveryNotesService.getConsigneeDeliveryNotePdfsForBundle(
                    {
                        organizationSiteKey: bundle.accessSiteKey,
                        deliveryNoteBundleKey: bundle._key,
                    }
                )
            );

            const url = window.URL.createObjectURL(response);
            const a = document.createElement('a');
            a.setAttribute('style', 'display:none;');
            document.body.appendChild(a);
            a.href = url;
            const bundleNumber = bundle.bundleNumber;
            a.download = bundleNumber.replace('.', '-') + '.pdf';
            a.click();
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    viewFolderAsPdf(bundle: FolderData): void {
        this.dialog.open(ViewBundleDialogComponent, {
            data: {
                deliveryNoteBundle: bundle,
                organizationSiteKey: bundle.accessSiteKey,
                context: 'consignee',
            },
            disableClose: true,
            width: '800px',
        });
    }

    /**
     * Downloads a single delivery note PDF
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle: FolderData (optional) - The parent bundle containing the delivery note
     */
    async downloadDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData
    ): Promise<void> {
        this.spinner.show();
        try {
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            let response: StrictHttpResponse<Blob>;
            if (hasConsigneePdf()) {
                response = await lastValueFrom(
                    this.pdfServiceAddons.getConsigneeDeliveryNoteCombinedPdf$Response(
                        {
                            organizationSiteKey: organizationSiteKey,
                            deliveryNoteKey: deliveryNote._key,
                        }
                    )
                );
            } else {
                response = await lastValueFrom(
                    this.pdfService.getConsigneeDeliveryNoteCombinedPdf$Response(
                        {
                            organizationSiteKey: organizationSiteKey,
                            deliveryNoteKey: deliveryNote._key,
                        }
                    )
                );
            }

            const filename =
                response.headers.get('content-disposition')?.split('=')[1] ||
                deliveryNote.fileName;
            downloadBlobAsFile(response.body, filename);
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Views a single delivery note in a dialog
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle: FolderData (optional) - The parent bundle containing the delivery note
     */
    async viewDeliveryNote(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData
    ): Promise<void> {
        this.spinner.show();
        try {
            const organizationSiteKey =
                parentBundle?.accessSiteKey ||
                deliveryNote.accessSiteKey ||
                this.appStore.currentOrganizationSiteKey();

            if (!organizationSiteKey) {
                this.toastService.error(
                    this.translateService.instant('GENERAL-ERROR')
                );
                return;
            }

            let blobData: Blob;
            if (hasConsigneePdf()) {
                blobData = await lastValueFrom(
                    this.pdfServiceAddons.getConsigneeDeliveryNoteCombinedPdf({
                        organizationSiteKey: organizationSiteKey,
                        deliveryNoteKey: deliveryNote._key,
                    })
                );
            } else {
                blobData = await lastValueFrom(
                    this.pdfService.getConsigneeDeliveryNoteCombinedPdf({
                        organizationSiteKey: organizationSiteKey,
                        deliveryNoteKey: deliveryNote._key,
                    })
                );
            }

            this.dialog.open(ShowPdfDialogComponent, {
                minWidth: '80%',
                height: '95%',
                disableClose: true,
                data: {
                    data: blobData,
                },
            });
        } catch (error) {
            this.errorHandler.handleError(error);
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Opens a file picker to add fine discrepancy for a delivery note
     * @param deliveryNote: DeliveryNoteData
     * @param parentBundle?: FolderData | TourData - Optional parent bundle/tour for context
     */
    addFineDiscrepancy(
        deliveryNote: DeliveryNoteData,
        parentBundle?: FolderData | TourData
    ): void {
        // Create a file input element
        const fileInput = document.createElement('input');
        fileInput.type = 'file';
        fileInput.multiple = true;
        fileInput.accept = '.pdf,.jpg,.jpeg,.png,.heic';

        fileInput.onchange = async (event: Event) => {
            const files = (event.target as HTMLInputElement).files;
            if (files && files.length > 0) {
                this.spinner.show();
                try {
                    for (let i = 0; i < files.length; i++) {
                        const file = files[i];
                        const filename: string = file.name;
                        // Use parent bundle's accessSiteKey first, then delivery note's accessSiteKey, then fallback to current org key
                        const organizationSiteKey =
                            (parentBundle && 'accessSiteKey' in parentBundle
                                ? parentBundle.accessSiteKey
                                : undefined) ||
                            deliveryNote.accessSiteKey ||
                            this.appStore.currentOrganizationSiteKey();

                        if (!organizationSiteKey) {
                            this.toastService.error(
                                this.translateService.instant('GENERAL-ERROR')
                            );
                            return;
                        }

                        await lastValueFrom(
                            this.fineDiscrepanciesService.postNewFineDiscrepancy(
                                {
                                    organizationSiteKey: organizationSiteKey,
                                    deliveryNoteKey: deliveryNote._key,
                                    fileName: filename,
                                    body: file,
                                }
                            )
                        );
                    }

                    this.toastService.success(
                        this.translateService.instant(
                            'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-ADDED'
                        )
                    );

                    // Trigger data refresh to reload the table
                    this.searchStore.triggerDataRefresh();
                } catch (error: any) {
                    if (error?.status === 409) {
                        // Conflict - fine discrepancy already exists
                        this.toastService.warning(
                            this.translateService.instant(
                                'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-EXISTS'
                            )
                        );
                    } else {
                        this.toastService.error(
                            this.translateService.instant(
                                'INCOMING-GOODS.CHECKIN.FINE-DISCREPANCY-ADDED-ERROR'
                            )
                        );
                        this.errorHandler.handleError(error);
                    }
                } finally {
                    this.spinner.hide();
                }
            }
        };

        // Trigger the file picker
        fileInput.click();
    }

    async confirmDespiteActiveSelfCheckin(
        checkins: readonly SelfCheckinLookupEntry[] | null | undefined
    ): Promise<boolean> {
        if (!hasActiveSelfCheckin(checkins)) {
            return true;
        }

        const dialogRef = this.dialog.open(QuestionDialogComponent, {
            width: '480px',
            data: {
                icon: 'warning',
                headline: 'INCOMING-GOODS.CHECKIN.SELF-CHECKIN-ACTIVE.HEADLINE',
                msg: 'INCOMING-GOODS.CHECKIN.SELF-CHECKIN-ACTIVE.MESSAGE',
                confirmText:
                    'INCOMING-GOODS.CHECKIN.SELF-CHECKIN-ACTIVE.CONTINUE',
                cancelText: 'GENERAL.CANCEL',
            },
        });

        const result = await lastValueFrom(dialogRef.afterClosed());
        return result === QuestionCloseResult.YES;
    }

    /**
     * Check in delivery notes from selected tours
     * @param selectedTours: TourData[] - Array of selected tour objects
     */
    async checkinTourDeliveryNotes(selectedTours: TourData[]): Promise<void> {
        if (!selectedTours || selectedTours.length === 0) {
            return;
        }

        const siteKey: string = selectedTours[0].orgSiteKey;

        try {
            const keys =
                await this.getDeliveryNoteKeysFromSelectedTours(selectedTours);

            this.apiInterceptor.disableToastService();
            const response = await lastValueFrom(
                this.checkinsService.getConsigneeCheckinLookup({
                    organizationSiteKey: siteKey,
                    body: keys,
                })
            );
            this.apiInterceptor.enableToastService();

            if (
                !(await this.confirmDespiteActiveSelfCheckin(response.checkins))
            ) {
                return;
            }

            // Check if there are no valid checkin keys
            const hasValidCheckinKey = response.deliveryNotes.some(
                (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                    note.checkinKey && note.checkinKey.trim() !== ''
            );

            if (!hasValidCheckinKey && response.checkins.length === 0) {
                throw new Error('No valid checkin keys found');
            }

            // Extract checkin keys from both deliveryNotes and checkins
            const deliveryNoteCheckinKeys = response.deliveryNotes
                .map(
                    (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                        note.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');
            const checkinCheckinKeys = response.checkins
                .map(
                    (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                        checkin.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');

            // Combine both arrays to check for consistency
            const allCheckinKeys = [
                ...deliveryNoteCheckinKeys,
                ...checkinCheckinKeys,
            ];

            const allCheckinKeysSame = allCheckinKeys.every(
                key => key === allCheckinKeys[0]
            );

            // Check if deliveryNoteKeysExact is false in any of the checkins
            const deliveryNoteKeysExact = response.checkins.every(
                (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                    checkin.deliveryNoteKeysExact
            );

            if (allCheckinKeysSame && deliveryNoteKeysExact) {
                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: allCheckinKeys[0],
                            tourKey: selectedTours[0]._key,
                        },
                    }
                );
            } else {
                throw new Error();
            }
        } catch (error) {
            this.apiInterceptor.enableToastService();
            try {
                const keys =
                    await this.getDeliveryNoteKeysFromSelectedTours(
                        selectedTours
                    );
                const response = await lastValueFrom(
                    this.checkinsV3Service.postConsigneeAddonsCheckinV3({
                        organizationSiteKey: siteKey,
                        body: keys,
                    })
                );

                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: response._key,
                            tourKey: selectedTours[0]._key,
                        },
                    }
                );
            } catch (e: any) {
                if (e.status === 409) {
                    this.toastService.error(e.error.errorMessage);
                } else {
                    this.toastService.error(
                        this.translateService.instant(
                            'INCOMING-GOODS.CHECKIN.ERROR'
                        )
                    );
                }
            }
        }
    }

    private async getDeliveryNoteKeysFromSelectedTours(
        selectedTours: TourData[]
    ): Promise<string[]> {
        const promises: Array<Promise<GetConsigneeTour>> = [];

        selectedTours.forEach(tour => {
            promises.push(
                lastValueFrom(
                    this.toursService.getConsigneeTour({
                        organizationSiteKey: tour.orgSiteKey,
                        tourKey: tour._key,
                    })
                )
            );
        });

        const tours = await Promise.all(promises);

        const keys: Array<string> = [];

        tours.forEach(tour => {
            tour.deliveryNotes.forEach(note => {
                keys.push(note._key);
            });
        });

        return keys;
    }

    /**
     * Navigates to the check-in page for a notification event
     * @param event The notification event row data
     */
    async openCheckInPage(event: ApiEvent): Promise<void> {
        // Check if the event is a CheckinEvent
        if (hasCheckinKey(event.payload)) {
            try {
                this.apiInterceptor.disableToastService();
                const response = await lastValueFrom(
                    this.checkinsService.getConsigneeCheckin({
                        organizationSiteKey: event.organizationSiteKey,
                        checkinKey: event.payload.checkinKey,
                    })
                );
                this.apiInterceptor.enableToastService();
                if (response && response.status === 'finished') {
                    this.toastService.info(
                        this.translateService.instant(
                            'SELFSERVICE.PROCESS_FINISHED'
                        )
                    );
                } else {
                    // Switch to the notification's location before navigating
                    this.switchToEventLocation(event.organizationSiteKey);

                    const organizationSiteKey = event.organizationSiteKey;
                    const checkinKey = event.payload.checkinKey;
                    const url = `/consignee/checkin-notes?organizationSiteKey=${organizationSiteKey}&checkinKey=${checkinKey}`;
                    this.router.navigateByUrl(url);
                }
            } catch (error) {
                this.toastService.info(
                    this.translateService.instant(
                        'SELFSERVICE.PROCESS_NOT_ACCESSIBLE'
                    )
                );
            }
        }
        this.apiInterceptor.enableToastService();
    }

    /**
     * Switches to the specified organization site if it differs from the current site.
     * Uses individual store setters to avoid triggering data refresh.
     * @param targetOrgSiteKey The organization site key to switch to
     */
    private switchToEventLocation(targetOrgSiteKey: string): void {
        const currentOrgSiteKey = this.appStore.currentOrganizationSiteKey();

        // Only switch if the event is from a different location
        if (targetOrgSiteKey && targetOrgSiteKey !== currentOrgSiteKey) {
            const session = this.sessionService.getSession();
            const targetSite = session.organizationSites.find(
                site => site._key === targetOrgSiteKey
            );

            if (targetSite) {
                // Use same role determination logic as user-profile.component.ts
                let role = 'consignee';
                if (targetSite.roles.includes(UserRoles.RemoteConsignee)) {
                    role = 'remote_consignee';
                }

                const siteRole: CurrentSiteRole = {
                    role: role,
                    siteInfo: targetSite,
                };

                // Use INDIVIDUAL setters (no data refresh) to avoid table reload
                this.appStore.setCurrentOrganizationSite(targetSite);
                this.appStore.setCurrentSiteRole(siteRole);

                // Update session service observables (triggers header update)
                this.sessionService.currentSiteRole.set(siteRole);
                this.sessionService.setCurrentOrganizationSite(targetSite._key);

                // Persist to localStorage
                this.storageService.setItem('currentSiteRole', siteRole);
            }
        }
    }

    /**
     * Check in delivery note bundles from selected folders
     * @param selectedBundles: FolderData[] - Array of selected bundle objects
     */
    async checkinDeliveryNoteBundles(
        selectedBundles: FolderData[]
    ): Promise<void> {
        if (!selectedBundles || selectedBundles.length === 0) {
            return;
        }

        // Grouped bundles are checked in via their bundle group only.
        if (this.hasGroupedBundles(selectedBundles)) {
            this.toastService.error(
                this.translateService.instant(
                    'INCOMING-GOODS.BUNDLE-GROUPS.CHECKIN-BLOCKED-BY-GROUP'
                )
            );
            return;
        }

        const siteKey: string = selectedBundles[0].accessSiteKey;
        const keys =
            await this.getDeliveryNoteKeysFromSelectedBundles(selectedBundles);

        try {
            this.apiInterceptor.disableToastService();
            const response = await lastValueFrom(
                this.checkinsService.getConsigneeCheckinLookup({
                    organizationSiteKey: siteKey,
                    body: keys,
                })
            );
            this.apiInterceptor.enableToastService();

            if (
                !(await this.confirmDespiteActiveSelfCheckin(response.checkins))
            ) {
                return;
            }

            // Check if there are no valid checkin keys
            const hasValidCheckinKey = response.deliveryNotes.some(
                (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                    note.checkinKey && note.checkinKey.trim() !== ''
            );

            if (!hasValidCheckinKey && response.checkins.length === 0) {
                throw new Error('No valid checkin keys found');
            }

            // Extract checkin keys from both deliveryNotes and checkins
            const deliveryNoteCheckinKeys = response.deliveryNotes
                .map(
                    (note: DeliveryNoteKeyAndCheckinKeyLookup) =>
                        note.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');
            const checkinCheckinKeys = response.checkins
                .map(
                    (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                        checkin.checkinKey
                )
                .filter((key: string) => key && key.trim() !== '');

            // Combine both arrays to check for consistency
            const allCheckinKeys = [
                ...deliveryNoteCheckinKeys,
                ...checkinCheckinKeys,
            ];

            const allCheckinKeysSame = allCheckinKeys.every(
                key => key === allCheckinKeys[0]
            );

            // Check if deliveryNoteKeysExact is false in any of the checkins
            const deliveryNoteKeysExact = response.checkins.every(
                (checkin: CheckinKeyAndDeliveryNoteKeyLookup) =>
                    checkin.deliveryNoteKeysExact
            );

            if (allCheckinKeysSame && deliveryNoteKeysExact) {
                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: allCheckinKeys[0],
                            checkinBundleKeys:
                                this.getCheckinBundleKeys(selectedBundles),
                        },
                    }
                );
            } else {
                throw new Error(
                    'Inconsistent checkin keys or deliveryNoteKeysExact is false'
                );
            }
        } catch (error) {
            this.apiInterceptor.enableToastService();
            try {
                const response = await lastValueFrom(
                    this.checkinsV3Service.postConsigneeAddonsCheckinV3({
                        organizationSiteKey: siteKey,
                        body: keys,
                    })
                );

                this.router.navigate(
                    [
                        RoutingEndpoints.consignee,
                        RoutingEndpoints.consignee_checkin_notes,
                    ],
                    {
                        queryParams: {
                            organizationSiteKey: siteKey,
                            checkinKey: response._key,
                            checkinBundleKeys:
                                this.getCheckinBundleKeys(selectedBundles),
                        },
                    }
                );
            } catch (e: any) {
                if (e.status === 409) {
                    this.toastService.error(e.error.errorMessage);
                } else {
                    this.toastService.error(
                        this.translateService.instant(
                            'INCOMING-GOODS.CHECKIN.ERROR'
                        )
                    );
                }
            }
        }
    }

    private async getDeliveryNoteKeysFromSelectedBundles(
        selectedBundles: FolderData[]
    ): Promise<string[]> {
        const promises: Array<Promise<ConsigneeDeliveryNoteBundle>> = [];

        selectedBundles.map(bundle => {
            promises.push(
                lastValueFrom(
                    this.deliveryNoteBundlesService.getConsigneeBundle({
                        organizationSiteKey: bundle.accessSiteKey,
                        deliveryNoteBundleKey: bundle._key,
                    })
                )
            );
        });

        const bundles = await Promise.all(promises);

        const keys: Array<string> = [];

        bundles.forEach(bundle => {
            bundle.deliveryNotes.forEach(note => {
                if (!note.dateReceived) {
                    keys.push(note._key);
                }
            });
        });

        return keys;
    }

    private getCheckinBundleKeys(bundles: FolderData[]): string[] {
        const checkinBundleKeys: string[] = [];
        bundles.map(bundle => {
            checkinBundleKeys.push(bundle._key);
        });
        return checkinBundleKeys;
    }

    /**
     * True when at least one of the given bundles already belongs to a bundle
     * group (Mappengruppe). Such bundles can neither be grouped again nor be
     * checked in individually.
     */
    hasGroupedBundles(selectedBundles: FolderData[]): boolean {
        return (selectedBundles ?? []).some(bundle => !!bundle.bundleGroupKey);
    }

    /**
     * Client-side pre-check whether the selected folders can be grouped into a
     * bundle group (Mappengruppe). Requires at least two folders that belong to
     * the same supply chain (identical access site, consignor, carrier and
     * consignee), that are not yet checked in (status !== 'closed') and that are
     * not already part of another group. The "already grouped" guard is
     * additionally enforced server-side (409).
     */
    canGroupBundles(selectedBundles: FolderData[]): boolean {
        if (!selectedBundles || selectedBundles.length < 2) {
            return false;
        }

        if (this.hasGroupedBundles(selectedBundles)) {
            return false;
        }

        const [first, ...rest] = selectedBundles;
        if (first.status === 'closed') {
            return false;
        }

        return rest.every(
            bundle =>
                bundle.status !== 'closed' &&
                bundle.accessSiteKey === first.accessSiteKey &&
                bundle.consignorKey === first.consignorKey &&
                bundle.carrierKey === first.carrierKey &&
                bundle.consigneeKey === first.consigneeKey
        );
    }

    /**
     * Create a supply-chain bundle group (Mappengruppe) from the selected
     * folders and switch to the bundle groups view to show the result.
     */
    async groupBundles(selectedBundles: FolderData[]): Promise<void> {
        if (!this.canGroupBundles(selectedBundles)) {
            return;
        }

        const organizationSiteKey = selectedBundles[0].accessSiteKey;
        const bundleKeys = selectedBundles.map(bundle => bundle._key);

        try {
            this.spinner.show();
            await lastValueFrom(
                this.bundleGroupsService.createBundleGroup({
                    organizationSiteKey,
                    body: {
                        bundleKeys,
                    },
                })
            );

            this.toastService.success(
                this.translateService.instant(
                    'INCOMING-GOODS.BUNDLE-GROUPS.CREATE-SUCCESS'
                )
            );

            // Switch to the bundle groups tab and reload it.
            this.searchStore.updateCurrentViewType('bundlegroups');
            this.searchStore.triggerDataRefresh();
        } catch (e: any) {
            if (e?.status === 409) {
                this.toastService.error(
                    e?.error?.errorMessage ||
                        this.translateService.instant(
                            'INCOMING-GOODS.BUNDLE-GROUPS.CREATE-CONFLICT'
                        )
                );
            } else {
                this.toastService.error(
                    this.translateService.instant(
                        'INCOMING-GOODS.BUNDLE-GROUPS.CREATE-ERROR'
                    )
                );
            }
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Start the check-in of a single bundle group (Mappengruppe) via
     * BundleGroupsService.checkinBundleGroup. On success the returned checkinKey
     * is used to open the existing consignee check-in view (the same view used
     * by the folders / tours / delivery notes check-in flows). A 409 conflict is
     * surfaced with the server message, other errors with a generic check-in
     * error message.
     */
    async checkinBundleGroup(group: BundleGroupData): Promise<void> {
        if (!group) {
            return;
        }

        try {
            this.spinner.show();
            const response = await lastValueFrom(
                this.bundleGroupsService.checkinBundleGroup({
                    organizationSiteKey: group.orgSiteKey,
                    groupKey: group._key,
                })
            );

            if (!response?.checkinKey) {
                throw new Error('No checkinKey returned for bundle group');
            }

            this.selectionStore.clearSelection();
            this.router.navigate(
                [
                    RoutingEndpoints.consignee,
                    RoutingEndpoints.consignee_checkin_notes,
                ],
                {
                    queryParams: {
                        organizationSiteKey: group.orgSiteKey,
                        checkinKey: response.checkinKey,
                        bundleGroupKey: group._key,
                    },
                }
            );
        } catch (e: any) {
            if (e?.status === 409) {
                this.toastService.error(
                    e?.error?.errorMessage ||
                        this.translateService.instant(
                            'INCOMING-GOODS.BUNDLE-GROUPS.CHECKIN-CONFLICT'
                        )
                );
            } else {
                this.toastService.error(
                    this.translateService.instant(
                        'INCOMING-GOODS.BUNDLE-GROUPS.CHECKIN-ERROR'
                    )
                );
            }
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Whether the given bundle group may be dissolved. Only groups that have
     * not entered the check-in process yet can be ungrouped (req #17).
     */
    canUngroupBundleGroup(group: BundleGroupData | undefined): boolean {
        return !!group && group.status === 'open' && !group.read_only;
    }

    /**
     * Dissolve a bundle group (Mappengruppe). Data recorded on the group's
     * virtual delivery note is discarded by the backend, so the user has to
     * confirm first (req #19).
     */
    async ungroupBundleGroup(group: BundleGroupData): Promise<void> {
        if (!this.canUngroupBundleGroup(group)) {
            return;
        }

        const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
            minWidth: '460px',
            maxWidth: '460px',
            disableClose: true,
            data: {
                icon: 'inventory_2',
                headline: this.translateService.instant(
                    'INCOMING-GOODS.BUNDLE-GROUPS.UNGROUP-CONFIRM.HEADLINE'
                ),
                msg: this.translateService.instant(
                    'INCOMING-GOODS.BUNDLE-GROUPS.UNGROUP-CONFIRM.DESCRIPTION',
                    { name: group.name }
                ),
            },
        });

        const result = await lastValueFrom(dialogRef.afterClosed());
        if (result !== 'save') {
            return;
        }

        try {
            this.spinner.show();
            await lastValueFrom(
                this.bundleGroupsService.deleteBundleGroup({
                    organizationSiteKey: group.orgSiteKey,
                    groupKey: group._key,
                })
            );

            this.toastService.success(
                this.translateService.instant(
                    'INCOMING-GOODS.BUNDLE-GROUPS.UNGROUP-SUCCESS'
                )
            );

            this.selectionStore.clearSelection();
            this.bundleGroupsStore.removeBundleGroup(group._key);
            this.searchStore.triggerDataRefresh();
        } catch (e: any) {
            if (e?.status === 409) {
                this.toastService.error(
                    e?.error?.errorMessage ||
                        this.translateService.instant(
                            'INCOMING-GOODS.BUNDLE-GROUPS.UNGROUP-CONFLICT'
                        )
                );
            } else {
                this.toastService.error(
                    this.translateService.instant(
                        'INCOMING-GOODS.BUNDLE-GROUPS.UNGROUP-ERROR'
                    )
                );
            }
        } finally {
            this.spinner.hide();
        }
    }

    /**
     * Opens the scan wallet page for QR code scanning
     */ scanWallet(): void {
        this.router.navigate(
            [
                RoutingEndpoints.consignee,
                RoutingEndpoints.consignee_checkin_notes,
            ],
            {
                queryParams: {
                    scan: true,
                    organizationSiteKey:
                        this.appStore.currentOrganizationSiteKey(),
                },
            }
        );
    }
}
